import { memo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import DropZone from '../../base-ui/components/DropZone';
import DropBox from '../../main/component/common/DropBox';
import SpinnerIcon from '../../main/component/icon/SpinnerIcon';
import { baseUrl } from '../../main/client';

interface Props {
    open: boolean;
    onClose: () => void;
}

const UploadInvoicesDialog = memo(({ open, onClose }: Props) => {
    const [isFileDraggedIn, setIsFileDraggedIn] = useState(false);
    const [uploadState, setUploadState] = useState<
        undefined | 'uploading' | 'processing' | Error
    >(undefined);

    const uploadInvoiceFile = (invoiceFileExport: File) => {
        setUploadState('uploading');
        (async () => {
            const formData = new FormData();
            formData.append('file', invoiceFileExport);

            const token = localStorage.getItem('token');

            const response = await fetch(`${baseUrl}/api/upload-invoice-list`, {
                method: 'POST',
                headers: {
                    authorization: token ? `bearer ${token}` : '',
                },
                body: formData,
            });

            if (response.status === 401) {
                localStorage.removeItem('token');
                window.location.reload();
                return;
            }

            if (!response.ok) {
                let message = response.statusText;
                try {
                    const body = await response.json();
                    if (body.error) {
                        message = body.error;
                    }
                } catch {
                    // Fall back to statusText
                }
                setUploadState(new Error(message));
                return;
            }

            const { jobId } = await response.json();
            setUploadState('processing');

            const poll = async () => {
                const statusResponse = await fetch(`${baseUrl}/api/invoice-import-jobs/${jobId}`, {
                    headers: {
                        authorization: token ? `bearer ${token}` : '',
                    },
                });

                if (!statusResponse.ok) {
                    setUploadState(new Error('Failed to check import status'));
                    return;
                }

                const job = await statusResponse.json();

                if (job.status === 'completed') {
                    setUploadState(undefined);
                    onClose();
                    return;
                }

                if (job.status === 'failed') {
                    setUploadState(new Error(job.error ?? 'Import failed'));
                    return;
                }

                setTimeout(poll, 2000);
            };

            await poll();
        })().catch(error => {
            setUploadState(error);
        });
    };

    const handleClose = () => {
        if (uploadState === 'uploading' || uploadState === 'processing') return;
        setUploadState(undefined);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 0,
                    border: '3px solid #6366f1',
                    backgroundColor: '#1a1a2e',
                },
            }}
        >
            <DialogTitle
                sx={{
                    fontWeight: 900,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '3px solid #6366f1',
                }}
            >
                Upload Invoices
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
                <Box sx={{ mt: 2 }}>
                    {uploadState === 'uploading' || uploadState === 'processing' ? (
                        <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={1}>
                            <SpinnerIcon />
                            <Typography variant="body2" sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em', color: '#9ca3af' }}>
                                {uploadState === 'uploading' ? 'Uploading...' : 'Processing...'}
                            </Typography>
                        </Box>
                    ) : uploadState ? (
                        <Alert
                            severity="error"
                            onClose={() => setUploadState(undefined)}
                            sx={{
                                mb: 2,
                                borderRadius: 0,
                                border: '3px solid #ef4444',
                                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                fontWeight: 600,
                            }}
                        >
                            {uploadState.message}
                        </Alert>
                    ) : (
                        <DropZone
                            onDragEnter={() => setIsFileDraggedIn(true)}
                            onDragLeave={() => setIsFileDraggedIn(false)}
                            onDropFiles={files => {
                                setIsFileDraggedIn(false);
                                const file = files.item(0);
                                if (file) {
                                    uploadInvoiceFile(file);
                                }
                            }}
                        >
                            <DropBox highlight={isFileDraggedIn}>
                                Drop &quot;Pénzforgalom (Excel)&quot; document.
                            </DropBox>
                        </DropZone>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
});

export default UploadInvoicesDialog;
